import { Calendar } from "@ttc3k/trekker";
import { Box } from "styled-system/jsx";
import { DateRange } from "../../Steps/components/DatesAndGuests/useDatesAndGuests";
import { ConsumingOverlayPageProps, OverlayPage } from "../OverlayPage";
import { convertDateRangeToCalendarValue } from "./helpers";
import { useBookerContext } from "widgets/booker/contexts";
import {
  EnumRuleType,
  useInventoryByProductBookedDatesQuery,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { formatToISO8601, parseCalendarValueAsString } from "shared/utils";

type DatesPageProps = ConsumingOverlayPageProps & {
  selectedDates: DateRange;
  onUpdateDates: (dateRange: Partial<DateRange>) => void;
  completeDates: () => void;
  resetDates: () => void;
};

export const DatesPage = ({
  selectedDates,
  onUpdateDates,
  completeDates,
  resetDates,
  onClose,
  ...rest
}: DatesPageProps) => {
  const { product, loading: contextLoading, booker } = useBookerContext();

  const { t } = useTranslation("booker", { keyPrefix: "DATES_PAGE" });

  if (!product && !contextLoading) {
    throw new Error("Error loading accommodation calendar");
  }

  const { data, error, loading } = useInventoryByProductBookedDatesQuery({
    variables: { productId: product?._id ?? "" },
  });

  if ((!data && !loading) || error) {
    throw new Error("Error loading inventory booked dates");
  }

  const isDatesPageValid = Object.values(selectedDates).every(
    v => v.length > 0,
  );

  const handleButtonClick = () => {
    completeDates();
    onClose();
  };

  const handleBackClick = () => {
    resetDates();
    onClose();
  };

  const handleClearDates = () => {
    onUpdateDates({ startDateISO: "", endDateISO: "" });
  };

  const handleValueChange = (dates: string[]) => {
    if (dates.length > 1) {
      onUpdateDates({ startDateISO: dates[0], endDateISO: dates[1] });
    } else if (dates.length === 1) {
      onUpdateDates({ startDateISO: dates[0], endDateISO: "" });
    } else {
      handleClearDates();
    }
  };

  const minNights =
    product?.rules.find(
      r => r.type === EnumRuleType.MinNights && !r.activePeriod,
    )?.value ?? undefined;
  const maxNights =
    product?.rules.find(
      r => r.type === EnumRuleType.MaxNights && !r.activePeriod,
    )?.value ?? undefined;
  const unavailableCheckInDays =
    product?.rules.find(
      r => r.type === EnumRuleType.UnavailableCheckInDays && !r.activePeriod,
    )?.value ?? undefined;
  const unavailableCheckOutDays =
    product?.rules.find(
      r => r.type === EnumRuleType.UnavailableCheckOutDays && !r.activePeriod,
    )?.value ?? undefined;
  const dateSpecificRules = product?.rules
    .filter(
      r =>
        !!r.activePeriod &&
        (r.type === EnumRuleType.MinNights ||
          r.type === EnumRuleType.MaxNights),
    )
    .map(r => ({
      startDateISO: r.activePeriod?.start ?? "",
      endDateISO: r.activePeriod?.end ?? "",
      minNights: r.type === EnumRuleType.MinNights ? r.value : undefined,
      maxNights: r.type === EnumRuleType.MaxNights ? r.value : undefined,
    }));
  const firstCalendarDateISO =
    booker?.rules.find(
      r => r.type === EnumRuleType.MinBookableDate && !r.activePeriod,
    )?.value ?? undefined;
  const lastCalendarDateISO =
    booker?.rules.find(
      r => r.type === EnumRuleType.MaxBookableDate && !r.activePeriod,
    )?.value ?? undefined;

  return (
    <OverlayPage
      title={t("PAGE_TITLE")}
      subtitle={t("SUBTITLE")}
      onButtonClick={handleButtonClick}
      onClose={handleBackClick}
      buttonDisabled={!isDatesPageValid}
      {...rest}
    >
      <Box width={"full"} height={{ base: "420px", md: "382px" }} mt={"400"}>
        <Calendar
          unavailableDatesISO={formatToISO8601(
            data?.inventoryDatesByProduct?.bookedDatesISO ?? [],
          )}
          checkOutOnlyDatesISO={formatToISO8601(
            data?.inventoryDatesByProduct?.checkOutOnlyDatesISO ?? [],
          )}
          minNights={minNights}
          maxNights={maxNights}
          unavailableCheckInDays={unavailableCheckInDays}
          unavailableCheckOutDays={unavailableCheckOutDays}
          dateSpecificRules={dateSpecificRules}
          firstCalendarDateISO={
            firstCalendarDateISO
              ? formatToISO8601(firstCalendarDateISO)
              : undefined
          }
          lastCalendarDateISO={
            lastCalendarDateISO
              ? formatToISO8601(lastCalendarDateISO)
              : undefined
          }
          onValueChange={({ valueAsString }: { valueAsString: string[] }) =>
            handleValueChange(parseCalendarValueAsString(valueAsString))
          }
          value={convertDateRangeToCalendarValue(selectedDates)}
          onClearDatesClick={handleClearDates}
          displayJumpToToday
        />
      </Box>
    </OverlayPage>
  );
};
